import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { ResourceDetailDialog } from '@/features/resources/components/ResourceDetailDialog';
import { ResourceCard } from '@/features/resources/components/ResourceCard';
import { ResourcesGridSkeleton } from '@/features/resources/components/ResourcesGridSkeleton';
import { useLatestResources } from '@/features/resources/hooks/useLatestResources';
import { useOpenResource } from '@/features/resources/hooks/useOpenResource';
import { ROUTES } from '@/lib/constants/routes';
import { DASHBOARD_PREVIEW, DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';

const S = DASHBOARD_STRINGS.RESOURCES;

/**
 * "Resources for you" on the member dashboard (MYN-254): the newest resources,
 * using the resources page's own cards and detail dialog so opening one
 * behaves exactly as it does there.
 */
export function DashboardResources() {
  const { data, isLoading, isError, refetch } = useLatestResources(DASHBOARD_PREVIEW.RESOURCES);
  const { dialogItem, openItem, closeDialog } = useOpenResource();
  const items = data?.items ?? [];

  return (
    <Card shadow="soft" hoverEffect={false} className="w-full">
      <section aria-labelledby="dashboard-resources-heading" className="flex flex-col gap-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <h2 id="dashboard-resources-heading" className="text-h3 text-teal">
              {S.TITLE}
            </h2>
            {data && data.total > 0 && (
              <Badge
                variant="green-solid"
                size="count"
                className="rounded-full px-2.5 py-0.5 text-sm font-bold"
              >
                {data.total}
              </Badge>
            )}
          </div>
          <Button asChild variant="link" className="text-[1.5rem] text-green hover:text-dkgreen">
            <Link to={ROUTES.RESOURCES}>{DASHBOARD_STRINGS.VIEW_ALL}</Link>
          </Button>
        </div>
        {isLoading && <ResourcesGridSkeleton />}
        {isError && <ErrorState description={S.LOAD_FAILED} onRetry={() => void refetch()} />}
        {!isLoading && !isError && items.length === 0 && <p className="text-body text-slate">{S.EMPTY}</p>}
        {items.length > 0 && (
          // Same container-width columns as the events block above, so the two
          // sections break to one column or three at the same card width.
          <div className="@container">
            <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-3">
              {items.map((item, index) => (
                <ResourceCard
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  description={item.description}
                  createdAt={item.createdAt}
                  externalUrl={item.externalUrl}
                  fileUrl={item.fileUrl}
                  coverImageUrl={item.coverImageUrl}trustedPartner={item.trustedPartner}
                  accentIndex={index}
                  onOpen={() => openItem(item)}
                  coverOnTop
                />
              ))}
            </div>
          </div>
        )}
      </section>
      <ResourceDetailDialog item={dialogItem} onClose={closeDialog} />
    </Card>
  );
}
